import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    Activity, Mail, Wallet, CheckCircle2, RefreshCw, ArrowRight,
    GraduationCap, Loader2, Sparkles,
} from "lucide-react";
import { toast } from "sonner";

const POLL_INTERVAL_MS = 30000;     // 30s background poll
const HIGHLIGHT_DURATION_MS = 8000; // pulse ring fades after 8s

function formatRelative(iso) {
    if (!iso) return "—";
    try {
        const then = new Date(iso).getTime();
        const now = Date.now();
        const diffSec = Math.max(0, Math.round((now - then) / 1000));
        if (diffSec < 60) return "just now";
        if (diffSec < 3600) return `${Math.round(diffSec / 60)}m ago`;
        if (diffSec < 86400) return `${Math.round(diffSec / 3600)}h ago`;
        if (diffSec < 30 * 86400) return `${Math.round(diffSec / 86400)}d ago`;
        return new Date(iso).toLocaleDateString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    } catch {
        return iso;
    }
}

function PaymentChip({ mode }) {
    const label = (mode || "online_stripe").replace(/_/g, " ");
    const cls =
        mode === "paid_offline"
            ? "border-primary/40 text-primary bg-primary/5"
            : mode === "waived"
              ? "border-accent/50 text-accent-foreground bg-accent/15"
              : "border-border text-muted-foreground bg-muted/30";
    return (
        <Badge variant="outline" className={`text-[10px] capitalize ${cls}`}>
            <Wallet className="h-2.5 w-2.5 mr-1" />
            {label}
        </Badge>
    );
}

export default function RecentEnrollmentsCard() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [resending, setResending] = useState(null);
    const [freshIds, setFreshIds] = useState(() => new Set());
    const [isLive, setIsLive] = useState(
        typeof document !== "undefined" ? !document.hidden : true,
    );
    const seenIdsRef = useRef(new Set());
    const isFirstLoadRef = useRef(true);
    const highlightTimersRef = useRef(new Map());

    const load = useCallback(async (opts = {}) => {
        const { silent = false } = opts;
        if (!silent) setLoading(true);
        try {
            const r = await api.get("/admin/recent-enrollments", { params: { limit: 10 } });
            const next = r.data || [];
            if (!isFirstLoadRef.current) {
                const prevIds = seenIdsRef.current;
                const arrivedIds = next
                    .map((it) => it.enrollment_id)
                    .filter((id) => !prevIds.has(id));
                if (arrivedIds.length > 0) {
                    setFreshIds((prev) => {
                        const merged = new Set(prev);
                        arrivedIds.forEach((id) => merged.add(id));
                        return merged;
                    });
                    arrivedIds.forEach((id) => {
                        if (highlightTimersRef.current.has(id)) {
                            window.clearTimeout(highlightTimersRef.current.get(id));
                        }
                        const t = window.setTimeout(() => {
                            setFreshIds((prev) => {
                                const merged = new Set(prev);
                                merged.delete(id);
                                return merged;
                            });
                            highlightTimersRef.current.delete(id);
                        }, HIGHLIGHT_DURATION_MS);
                        highlightTimersRef.current.set(id, t);
                    });
                    if (silent) {
                        const studentName = next.find(
                            (it) => it.enrollment_id === arrivedIds[0],
                        )?.student_name;
                        const summary =
                            arrivedIds.length === 1 && studentName
                                ? `${studentName} just enrolled`
                                : `${arrivedIds.length} new enrolments`;
                        toast.success(summary, { duration: 3000 });
                    }
                }
            }
            seenIdsRef.current = new Set(next.map((it) => it.enrollment_id));
            isFirstLoadRef.current = false;
            setItems(next);
        } catch {
            if (!silent) setItems([]);
        } finally {
            if (!silent) setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    // Background poll every 30s. Pause when tab is hidden to save battery + API calls.
    useEffect(() => {
        let interval = null;
        const start = () => {
            if (interval) return;
            interval = window.setInterval(() => {
                if (!document.hidden) load({ silent: true });
            }, POLL_INTERVAL_MS);
        };
        const stop = () => {
            if (interval) {
                window.clearInterval(interval);
                interval = null;
            }
        };
        const onVisibilityChange = () => {
            if (document.hidden) {
                stop();
                setIsLive(false);
            } else {
                load({ silent: true });
                start();
                setIsLive(true);
            }
        };
        start();
        document.addEventListener("visibilitychange", onVisibilityChange);
        return () => {
            stop();
            document.removeEventListener("visibilitychange", onVisibilityChange);
        };
    }, [load]);

    // Cleanup any pending highlight timers on unmount
    useEffect(
        () => () => {
            highlightTimersRef.current.forEach((t) => window.clearTimeout(t));
            highlightTimersRef.current.clear();
        },
        [],
    );

    const resend = async (item) => {
        setResending(item.enrollment_id);
        try {
            await api.post(`/admin/enrollments/${item.enrollment_id}/resend-welcome`);
            toast.success(`Welcome email re-sent to ${item.student_email}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not send email.");
        } finally {
            setResending(null);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="surface-card p-0 overflow-hidden"
            data-testid="recent-enrollments-card"
        >
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
                <div className="flex items-center gap-2">
                    <div className="h-8 w-8 rounded-md bg-primary/8 text-primary flex items-center justify-center">
                        <Activity className="h-4 w-4" />
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <h3 className="font-display text-lg text-foreground tracking-tight leading-none">
                                Recently enrolled
                            </h3>
                            {isLive && (
                                <span
                                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-semibold uppercase tracking-wider bg-sky-50 text-sky-700 border border-sky-200/70"
                                    data-testid="recent-enrollments-live-indicator"
                                    title="Auto-refreshing every 30s"
                                >
                                    <span className="relative flex h-1.5 w-1.5">
                                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-sky-500 opacity-70" />
                                        <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-sky-500" />
                                    </span>
                                    Live
                                </span>
                            )}
                        </div>
                        <p className="text-[11px] text-muted-foreground mt-1">
                            Last 10 enrolments — Stripe, walk-in, or admin-granted
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-1.5">
                    <Button
                        size="sm"
                        variant="ghost"
                        onClick={load}
                        disabled={loading}
                        className="h-8"
                        data-testid="recent-enrollments-refresh"
                        aria-label="Refresh"
                    >
                        <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
                    </Button>
                    <Button
                        asChild
                        size="sm"
                        variant="ghost"
                        className="h-8 gap-1 text-primary hover:text-primary"
                        data-testid="recent-enrollments-view-all"
                    >
                        <Link to="/admin/students">
                            View all
                            <ArrowRight className="h-3.5 w-3.5" />
                        </Link>
                    </Button>
                </div>
            </div>

            <div className="divide-y divide-border">
                {loading && items.length === 0 ? (
                    <div className="px-6 py-12 text-center text-sm text-muted-foreground flex items-center justify-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading recent enrolments…
                    </div>
                ) : items.length === 0 ? (
                    <div className="empty-state !py-10">
                        <div className="empty-icon-wrap !w-16 !h-16 !mb-3">
                            <Sparkles className="h-6 w-6 text-primary" />
                        </div>
                        <p className="font-display text-base text-foreground">
                            No enrolments yet
                        </p>
                        <p className="text-xs text-muted-foreground mt-1 max-w-sm">
                            Enrolments will surface here the moment a student joins via Stripe or
                            an admin-granted seat.
                        </p>
                        <Button
                            asChild
                            size="sm"
                            className="mt-4 bg-primary hover:bg-primary/90 gap-1.5"
                        >
                            <Link to="/admin/students">
                                <GraduationCap className="h-3.5 w-3.5" />
                                Enrol new student
                            </Link>
                        </Button>
                    </div>
                ) : (
                    items.map((it) => {
                        const isResending = resending === it.enrollment_id;
                        const isFresh = freshIds.has(it.enrollment_id);
                        return (
                            <motion.div
                                key={it.enrollment_id}
                                layout
                                initial={
                                    isFresh
                                        ? { opacity: 0, y: -8, backgroundColor: "rgba(212, 161, 64, 0.18)" }
                                        : false
                                }
                                animate={
                                    isFresh
                                        ? {
                                              opacity: 1,
                                              y: 0,
                                              backgroundColor: "rgba(212, 161, 64, 0)",
                                          }
                                        : { opacity: 1, y: 0 }
                                }
                                transition={{
                                    duration: isFresh ? 1.6 : 0.3,
                                    ease: [0.16, 1, 0.3, 1],
                                    backgroundColor: { duration: 4.5, ease: "easeOut" },
                                }}
                                className={`relative px-6 py-3.5 flex items-center gap-3 hover:bg-muted/30 transition-colors ${
                                    isFresh ? "ring-2 ring-inset ring-accent/45" : ""
                                }`}
                                data-testid={`recent-enrollment-row-${it.enrollment_id}`}
                                data-fresh={isFresh ? "true" : undefined}
                            >
                                {isFresh && (
                                    <span
                                        aria-hidden="true"
                                        className="absolute left-3 top-1/2 -translate-y-1/2 flex h-2 w-2"
                                    >
                                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent/70 opacity-75" />
                                        <span className="relative inline-flex rounded-full h-2 w-2 bg-accent" />
                                    </span>
                                )}
                                <div className={`h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center font-display text-sm ring-1 flex-shrink-0 ${
                                    isFresh ? "ring-accent/60" : "ring-primary/15"
                                }`}>
                                    {it.student_name?.charAt(0).toUpperCase() || "?"}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-medium text-sm text-foreground truncate">
                                            {it.student_name}
                                        </span>
                                        {isFresh && (
                                            <Badge
                                                variant="outline"
                                                className="text-[9px] border-accent/50 text-accent-foreground bg-accent/15 animate-pulse"
                                                data-testid="recent-enrollment-new-badge"
                                            >
                                                New
                                            </Badge>
                                        )}
                                        <PaymentChip mode={it.payment_mode} />
                                        {it.enrolled_by_admin && (
                                            <Badge
                                                variant="outline"
                                                className="text-[9px] border-accent/40 text-accent-foreground bg-accent/10"
                                                title={`Enrolled by ${it.enrolled_by_admin}`}
                                            >
                                                by {it.enrolled_by_admin.split(" ")[0]}
                                            </Badge>
                                        )}
                                    </div>
                                    <div className="text-xs text-muted-foreground mt-0.5 truncate">
                                        <span className="text-foreground/70">
                                            {it.program_title}
                                        </span>
                                        <span className="mx-1.5 opacity-50">·</span>
                                        <span>{formatRelative(it.enrolled_at)}</span>
                                    </div>
                                </div>
                                {it.completed_at ? (
                                    <Badge
                                        variant="outline"
                                        className="text-[10px] border-primary/40 text-primary bg-primary/5"
                                    >
                                        <CheckCircle2 className="h-3 w-3 mr-1" />
                                        Completed
                                    </Badge>
                                ) : (
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => resend(it)}
                                        disabled={isResending}
                                        className="h-8 gap-1.5 text-xs"
                                        data-testid={`recent-enrollment-resend-${it.enrollment_id}`}
                                    >
                                        {isResending ? (
                                            <Loader2 className="h-3 w-3 animate-spin" />
                                        ) : (
                                            <Mail className="h-3 w-3" />
                                        )}
                                        Resend welcome
                                    </Button>
                                )}
                            </motion.div>
                        );
                    })
                )}
            </div>
        </motion.div>
    );
}
